import { map } from 'nanostores';

/**
 * Panel size configuration for a layout.
 */
export interface PanelSizes {
  /** Size of the first panel (percentage, 0-100) */
  first: number;
  /** Size of the second panel (percentage, 0-100) */
  second: number;
}

/**
 * Storage key for all panel sizes.
 */
const STORAGE_KEY = 'lotus-panel-sizes';

/**
 * Load panel sizes from localStorage.
 */
function loadFromStorage(): Record<string, PanelSizes> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      return JSON.parse(stored) as Record<string, PanelSizes>;
    }
  } catch {
    // Invalid JSON or localStorage unavailable
  }
  return {};
}

/**
 * Save panel sizes to localStorage.
 */
function saveToStorage(data: Record<string, PanelSizes>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage full or unavailable
  }
}

/**
 * In-memory store for panel sizes.
 * Initialized from localStorage on load.
 */
export const $panelSizes = map<Record<string, PanelSizes>>(loadFromStorage());

// Sync to localStorage on every change
$panelSizes.subscribe((value) => {
  saveToStorage(value);
});

/**
 * Get panel sizes for a specific layout.
 *
 * @param layoutId - Unique identifier for the layout
 * @param defaultSizes - Default sizes if not stored
 * @returns The stored or default panel sizes
 */
export function getPanelSizes(
  layoutId: string,
  defaultSizes: PanelSizes = { first: 50, second: 50 }
): PanelSizes {
  return $panelSizes.get()[layoutId] ?? defaultSizes;
}

/**
 * Set panel sizes for a specific layout.
 *
 * @param layoutId - Unique identifier for the layout
 * @param sizes - The panel sizes to store
 */
export function setPanelSizes(layoutId: string, sizes: PanelSizes): void {
  $panelSizes.setKey(layoutId, sizes);
}
